import { useState, useRef, useEffect } from 'react';
import { AIAgent, ALL_AGENTS } from '@/types/aiAgents';
import { Button } from '@/components/ui/button';
import { ChevronDown } from "lucide-react";


interface AgentDropdownProps {
  agents: AIAgent[];
  onAgentSelect: (agent: AIAgent) => void;
  isDarkMode: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  onClose: () => void;
  searchQuery?: string;
  open: boolean;
  placement?: 'above' | 'bottom';
}

export const AgentDropdown = ({ 
  agents, 
  onAgentSelect, 
  isDarkMode, 
  inputRef, 
  onClose,
  searchQuery = "",
  open,
  placement = 'above',
}: AgentDropdownProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [shouldRender, setShouldRender] = useState(open);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Show scroll indicator if not scrolled to bottom
      setShowScrollIndicator(scrollTop + clientHeight < scrollHeight - 10);
    }
  };

  // Use ALL_AGENTS for real icons and short descriptions
  const allAgents = ALL_AGENTS;
  
  // Filter agents based on search query
  const filteredAgents = searchQuery.trim() === "" 
    ? allAgents 
    : allAgents.filter(agent => 
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchQuery.toLowerCase())
      );

  // Reset selected index when filtered agents change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      return;
    }

    const t = window.setTimeout(() => setShouldRender(false), 200);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredAgents.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredAgents[selectedIndex]) {
          onAgentSelect(filteredAgents[selectedIndex]);
          onClose();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, selectedIndex, filteredAgents, onAgentSelect, onClose]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!open) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, onClose]);

  if (!shouldRender) return null;

  return (
    <div
      ref={dropdownRef}
      className={`${
        placement === 'above'
          ? 'absolute bottom-full left-0 mb-2'
          : placement === 'bottom'
          ? 'absolute -top-36 left-0 mt-0'
          : 'w-full'
      } w-auto max-h-500 overflow-y-auto rounded-2xl border shadow-lg z-50 transform-gpu transition-all duration-200 ${
        open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
      } ${
        isDarkMode
          ? "bg-[#202020] backdrop-blur-xl border-[#303030]"
          : "bg-white/95 backdrop-blur-xl border-gray-300/70"
      }`}
    >
      <div 
        ref={contentRef}
        onScroll={handleScroll}
        className="p-2 sm:h-440 h-96 max-h-[max-content] w-72 relative"
      >
        <div className={`text-xs font-medium mb-2 px-2 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          {searchQuery ? `Searching for "${searchQuery}"` : 'Select an AI Agent'}
        </div>
        <hr className="mb-2 mt-1" />
        <div className="flex flex-col gap-0.5">
          {filteredAgents.length === 0 ? (
            <div className={`text-center py-4 px-2 ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>
              No agents found matching "{searchQuery}"
            </div>
          ) : (
            filteredAgents.map((agent, idx) => (
            <Button
              key={agent.id}
              variant={selectedIndex === idx ? 'secondary' : 'ghost'}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left transition-colors duration-150 ${
                selectedIndex === idx
                  ? isDarkMode
                    ? 'bg-[#303030] hover:bg-[#3c3c3c] text-white'
                    : 'bg-gray-200 text-gray-900'
                  : isDarkMode
                  ? 'text-gray-300 hover:bg-[#2c2c2c] hover:text-white'
                  : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
              }`}
              onClick={() => {
                onAgentSelect(agent);
                onClose();
              }}
            >
              <img src={agent.icon} alt={agent.name} className="w-8 h-8 object-contain" />
              <div className="flex-1 text-left">
                <div className="font-medium text-sm">{agent.name}</div>
                <div className="text-xs opacity-70">{agent.description}</div>
              </div>
            </Button>
          ))
          )}
          
          {/* Scroll indicator */}
          <div 
            className={`sticky bottom-2 left-0 right-0 flex justify-center transition-opacity duration-200 ${
              showScrollIndicator ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <ChevronDown 
              className={`w-4 h-4 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};
